import { LogTemplateType, useLogsTemplateQuery } from '@/__generated__/graphql';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import Box from '@mui/material/Box';
import {
  selectedCostLogsTemplate,
  selectedCostLogsTemplateField,
  selectedCostLogsTemplateId,
  costLogsTemplateFields,
  selectedWorklogsTemplateId,
  worklogsTemplateFields,
  selectedWorklogsTemplateField,
  selectedWorklogsTemplate,
} from '../state';
import { PageLoader } from '@/components/shared/PageLoader';
import { EmptyStatePlaceholder } from '@/components/Analysis/shared/EmptyStatePlaceholder';
import { FieldPanel } from '../TemplateForm/styles';
import { SharedLogsFieldsPanel } from './SharedLogsFieldsPanel';
import { KpiLogFormConfig } from './KPILogForm/KpiLogFormConfig';
import { BasicTabs } from '@/components/shared/BasicTabs';
import { workpackTabStyles, FloatingLine } from './styles';
import { DerivedFieldsForm } from './KPIDerivedFieldsForm/DerivedFieldsForm';
import { SharedLogsTemplateActions } from './SharedLogsTemplateActions';

type SharedLogsTemplateFormProps = {
  structureId: string;
  currentStructureName: string;
  emptyForm: boolean;
  handleEmptyForm: (value: boolean) => void;
  hostLogTemplateType: LogTemplateType;
};

export const SharedLogsTemplateForm = ({
  structureId,
  currentStructureName,
  emptyForm,
  handleEmptyForm,
  hostLogTemplateType,
}: SharedLogsTemplateFormProps) => {
  const worklogsTemplateId = useAtomValue(selectedWorklogsTemplateId);
  const costLogsTemplateId = useAtomValue(selectedCostLogsTemplateId);

  const hostLogsTemplateId =
    hostLogTemplateType === LogTemplateType.CostLogTemplate
      ? costLogsTemplateId
      : worklogsTemplateId;

  const setWorklogsTemplate = useSetAtom(selectedWorklogsTemplate);
  const setWorklogsTemplateFields = useSetAtom(worklogsTemplateFields);
  const setSelectedWorklogsTemplateField = useSetAtom(selectedWorklogsTemplateField);

  const setCostLogsTemplate = useSetAtom(selectedCostLogsTemplate);
  const setCostLogsTemplateFields = useSetAtom(costLogsTemplateFields);
  const setSelectedCostLogsTemplateField = useSetAtom(selectedCostLogsTemplateField);
  const [selectedTab, setSelectedTab] = useState<number>(0);

  const { data: worklogsTemplateData, loading } = useLogsTemplateQuery({
    variables: {
      logsTemplateId: hostLogsTemplateId ?? '',
    },
    skip: !hostLogsTemplateId,
  });

  useEffect(() => {
    if (worklogsTemplateData?.logsTemplate) {
      const nextTemplate = worklogsTemplateData.logsTemplate;
      const nextFields = nextTemplate.fields;

      if (hostLogTemplateType === LogTemplateType.CostLogTemplate) {
        setCostLogsTemplate(nextTemplate);
        setCostLogsTemplateFields(nextFields);
        setSelectedCostLogsTemplateField((previous) => {
          const previousId = previous?.id;
          const stillExists = previousId ? nextFields?.some((f) => f?.id === previousId) : false;
          return stillExists ? previous : nextFields?.at(0);
        });
      } else {
        setWorklogsTemplate(nextTemplate);
        setWorklogsTemplateFields(nextFields);
        setSelectedWorklogsTemplateField((previous) => {
          const previousId = previous?.id;
          const stillExists = previousId ? nextFields?.some((f) => f?.id === previousId) : false;
          return stillExists ? previous : nextFields?.at(0);
        });
      }
    }
  }, [
    hostLogTemplateType,
    worklogsTemplateData,
    setCostLogsTemplate,
    setCostLogsTemplateFields,
    setSelectedCostLogsTemplateField,
    setWorklogsTemplate,
    setWorklogsTemplateFields,
    setSelectedWorklogsTemplateField,
  ]);

  const handleTabChange = (selectedTabIndex: number) => {
    setSelectedTab(selectedTabIndex);
  };

  const tabs = useMemo(() => {
    const baseTabs: Array<{ index: number; label: string; component: ReactNode }> = [
      {
        index: 0,
        label: 'Fields',
        component: (
          <SharedLogsFieldsPanel
            emptyForm={emptyForm}
            handleEmptyForm={handleEmptyForm}
            structureId={structureId}
            hostLogTemplateType={hostLogTemplateType}
          />
        ),
      },
    ];
    return [
      ...baseTabs,
      {
        index: 1,
        label: 'KPI Configuration',
        component: (
          <KpiLogFormConfig structureId={structureId} hostLogTemplateType={hostLogTemplateType} />
        ),
      },
      {
        index: 2,
        label: 'KPI Derived Fields',
        component: (
          <DerivedFieldsForm
            structureId={structureId}
            hostLogsTemplateId={hostLogsTemplateId}
            hostLogsTemplate={worklogsTemplateData?.logsTemplate}
            hostLogTemplateType={hostLogTemplateType}
          />
        ),
      },
    ];
  }, [
    structureId,
    emptyForm,
    handleEmptyForm,
    hostLogsTemplateId,
    worklogsTemplateData?.logsTemplate,
    hostLogTemplateType,
  ]);

  let mainContent: ReactNode;
  if (hostLogsTemplateId) {
    if (loading) {
      mainContent = <PageLoader />;
    } else {
      mainContent = (
        <Box sx={{ pt: 3.5 }}>
          <BasicTabs
            tabs={tabs}
            selectedTab={selectedTab}
            changeTab={handleTabChange}
            tabsStyles={workpackTabStyles}
            customSeperator={<FloatingLine />}
            renderingStrategy="eager"
          />
        </Box>
      );
    }
  } else {
    mainContent = (
      <Box sx={{ pt: 2 }}>
        <FieldPanel justifyContent="center" alignItems="center" sx={{ width: '100%' }}>
          <EmptyStatePlaceholder type="template" message="No Templates Found" />
        </FieldPanel>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 3.5 }}>
      <SharedLogsTemplateActions
        structureId={structureId}
        currentStructureName={currentStructureName}
        linkedStructureIds={worklogsTemplateData?.logsTemplate?.linkedStructureIds ?? []}
      />
      {mainContent}
    </Box>
  );
};
